/**
 * ChatListFilterTabs Component
 *
 * Renders pill-shaped filter tabs for the Chat list, allowing users to filter by:
 * - All chats
 * - Flex chats (if not embedded)
 * - Jobs/Placements chats
 * - Support (triggers support flow)
 *
 * The Support pill has special handling to support the mid-shift intervention feature.
 * When clicked, it can either:
 * 1. Show the mid-shift intervention bottom sheet (if feature flag enabled AND shift in progress)
 * 2. Open Zendesk messaging directly (default behavior)
 *
 * This conditional behavior is controlled by the optional `onSupportClick` callback prop.
 */

import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { Tab, Tabs } from "@src/appV2/redesign/system/ui-components";

import { type ChatChannelCustomTypesFilter } from "../types";

interface ChatListFilterTabsProps {
  filterType: ChatChannelCustomTypesFilter;
  onFilterTypeChange: (newValue: ChatChannelCustomTypesFilter) => void;
  /**
   * Optional callback when Support pill is clicked.
   * If provided, this will be called instead of directly opening Zendesk.
   * This allows the parent component to conditionally show the mid-shift intervention
   * bottom sheet or open Zendesk based on feature flag and shift status.
   *
   * If not provided, falls back to default behavior of opening Zendesk messaging.
   */
  onSupportClick?: () => void;
}

export function ChatListFilterTabs({
  filterType,
  onFilterTypeChange,
  onSupportClick,
}: ChatListFilterTabsProps) {
  const { isEmbedded } = useEmbeddedMode();

  const handleSupportClick = () => {
    logEvent(APP_V2_USER_EVENTS.ENTERED_SUPPORT_FLOW_VIA_CHAT);
    if (onSupportClick) {
      onSupportClick();
    } else {
      // Fallback to default behavior (open Zendesk) if no callback provided
      void openZendeskMessaging();
    }
  };

  return (
    <Tabs
      variant="pill"
      value={filterType}
      onChange={(_, newValue: ChatChannelCustomTypesFilter) => {
        onFilterTypeChange(newValue);
      }}
    >
      <Tab variant="pill" label="All" value="all" />
      {!isEmbedded && <Tab variant="pill" label="Flex" value="flex" />}
      <Tab variant="pill" label="Jobs" value="placements" />
      <Tab variant="pill" label="Support" value="support" onClick={handleSupportClick} />
    </Tabs>
  );
}
